import { useEffect, useMemo, useState } from "react";
import axios from "axios";
import { API, getAuthToken } from "@/App";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { AlertCircle, Truck, Save, Gift } from "lucide-react";
import { toast } from "sonner";
import { toDateInput, toDMY } from "@/lib/dateOnly";

const isoToDateInput = (iso) => toDateInput(iso);

const toItemKey = (item, idx) => `${item.item_type || "sale"}::${item.sku_id || item.product_id || idx}`;

// Dispatch Sub-Status options (reason behind a reschedule). The first two are "green".
const SUB_STATUS_OPTIONS = [
  "On-Time Dispatch",
  "Pre-Pull",
  "Invoice Delay",
  "E-Way Bill Delay",
  "Incorrect Invoice",
  "Inventory Issues",
  "Manifestation Issues",
  "Transporter No-Show",
  "Others",
];

export default function DispatchEditModal({ open, dispatchId, onClose, onSaved }) {
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [quotation, setQuotation] = useState(null);
  const [qtyByKey, setQtyByKey] = useState({}); // {key: stringValue}
  const [status, setStatus] = useState("to_be_dispatch");
  const [pickupDate, setPickupDate] = useState("");
  const [rescheduledPickupDate, setRescheduledPickupDate] = useState("");
  const [subStatus, setSubStatus] = useState("On-Time Dispatch");
  const [subStatusReason, setSubStatusReason] = useState("");

  const isRescheduled = !!rescheduledPickupDate;

  // Keep Sub-Status consistent with the Rescheduled Pickup Date:
  //  - blank rescheduled  → always "On-Time Dispatch" (locked)
  //  - has rescheduled    → must pick a non-default reason (clear the default to force a choice)
  const handleRescheduledChange = (val) => {
    setRescheduledPickupDate(val);
    if (!val) {
      setSubStatus("On-Time Dispatch");
      setSubStatusReason("");
    } else if (subStatus === "On-Time Dispatch") {
      setSubStatus("");
    }
  };

  // Final Pickup Date is auto-derived (never edited): rescheduled if set, else original pickup.
  const finalPickupDate = rescheduledPickupDate || pickupDate;
  const formatDMY = (ymd) => toDMY(ymd);

  useEffect(() => {
    if (!open || !dispatchId) return;
    const fetchDetail = async () => {
      setLoading(true);
      try {
        const res = await axios.get(`${API}/dispatch/${dispatchId}`, {
          headers: { Authorization: `Bearer ${getAuthToken()}` },
        });
        const q = res.data;
        setQuotation(q);
        const map = {};
        // Default: blank Qty Sent. Persisted values (after a prior save) are still shown.
        // Users can fill manually or use "Select All" to copy Qty Planned across all SKUs.
        (q.items || []).forEach((it, idx) => {
          const k = toItemKey({ ...it, item_type: "sale" }, idx);
          map[k] = it.quantity_sent == null ? "" : String(it.quantity_sent);
        });
        (q.freebies || []).forEach((it, idx) => {
          const k = toItemKey({ ...it, item_type: "freebie" }, idx);
          map[k] = it.quantity_sent == null ? "" : String(it.quantity_sent);
        });
        setQtyByKey(map);
        setStatus(q.dispatch_status || "to_be_dispatch");
        setPickupDate(isoToDateInput(q.pickup_date));
        setRescheduledPickupDate(isoToDateInput(q.rescheduled_pickup_date));
        setSubStatus(q.dispatch_sub_status || "On-Time Dispatch");
        setSubStatusReason(q.dispatch_sub_status_reason || "");
      } catch {
        toast.error("Failed to load dispatch details");
        onClose?.();
      } finally {
        setLoading(false);
      }
    };
    fetchDetail();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, dispatchId]);

  const qtySentTotal = useMemo(() => {
    let sum = 0;
    let anySet = false;
    Object.values(qtyByKey).forEach((v) => {
      if (v === "" || v == null) return;
      const n = Number(v);
      if (Number.isFinite(n)) {
        sum += n;
        anySet = true;
      }
    });
    return anySet ? sum : null;
  }, [qtyByKey]);

  const qtyPlannedTotal = useMemo(() => {
    if (!quotation) return 0;
    let sum = 0;
    (quotation.items || []).forEach((it) => { sum += Number(it.quantity) || 0; });
    (quotation.freebies || []).forEach((it) => { sum += Number(it.quantity) || 0; });
    return sum;
  }, [quotation]);

  const handleQtyChange = (key, planned, value) => {
    if (value === "") {
      setQtyByKey((p) => ({ ...p, [key]: "" }));
      return;
    }
    const n = Number(value);
    if (!Number.isFinite(n)) return;
    if (n < 0) return;
    if (n > planned) {
      toast.error(`Qty Sent cannot exceed Qty Planned (${planned}) for this SKU.`);
      return;
    }
    setQtyByKey((p) => ({ ...p, [key]: value }));
  };

  // "Select All" — set Qty Sent = Qty Planned for every SKU (sale + freebie) in one click.
  const handleSelectAll = () => {
    if (!quotation) return;
    const next = {};
    (quotation.items || []).forEach((it, idx) => {
      const k = toItemKey({ ...it, item_type: "sale" }, idx);
      next[k] = String(Number(it.quantity) || 0);
    });
    (quotation.freebies || []).forEach((it, idx) => {
      const k = toItemKey({ ...it, item_type: "freebie" }, idx);
      next[k] = String(Number(it.quantity) || 0);
    });
    setQtyByKey(next);
    // Auto-advance to "Dispatched" on Select All (no manual status change needed).
    // Later partial edits (Qty Sent < Planned) keep the status Dispatched — red pending
    // highlighting remains the indicator, per existing logic.
    if (status !== "dispatched") setStatus("dispatched");
  };

  // Per-item short-circuits used by the table to highlight a partial-dispatch row in red.
  // A SKU is flagged when 0 ≤ qty_sent < qty_planned. Equal → no flag. Empty/null → no flag yet.
  const isPartial = (key, planned) => {
    const raw = qtyByKey[key];
    if (raw === "" || raw == null) return false;
    const n = Number(raw);
    return Number.isFinite(n) && n >= 0 && n < planned;
  };

  const validationErrors = useMemo(() => {
    const errs = [];
    if (status === "dispatched") {
      if (qtySentTotal == null || qtySentTotal <= 0) errs.push("Quantity Sent is required");
      if (!pickupDate) errs.push("Pickup Date is required");
    }
    return errs;
  }, [status, qtySentTotal, pickupDate]);

  const handleSave = async () => {
    if (validationErrors.length > 0) {
      toast.error("Cannot mark as Dispatched: " + validationErrors.join("; "));
      return;
    }
    if (!quotation) return;

    // Dispatch Sub-Status rules apply only once Dispatched: mandatory (and not the default) when
    // a reschedule exists; "Others" requires a reason. While "To be Dispatch" the field is hidden.
    if (status === "dispatched" && isRescheduled) {
      if (!subStatus || subStatus === "On-Time Dispatch") {
        toast.error("Select a Dispatch Sub-Status (On-Time Dispatch is not allowed when rescheduled)");
        return;
      }
      if (subStatus === "Others" && !subStatusReason.trim()) {
        toast.error("Reason is required when Dispatch Sub-Status is 'Others'");
        return;
      }
    }

    // Build per-SKU payload
    const payloadItems = [];
    (quotation.items || []).forEach((it, idx) => {
      const k = toItemKey({ ...it, item_type: "sale" }, idx);
      const raw = qtyByKey[k];
      payloadItems.push({
        sku_id: it.sku_id,
        item_type: "sale",
        quantity_sent: raw === "" ? null : Number(raw),
      });
    });
    (quotation.freebies || []).forEach((it, idx) => {
      const k = toItemKey({ ...it, item_type: "freebie" }, idx);
      const raw = qtyByKey[k];
      payloadItems.push({
        sku_id: it.sku_id,
        item_type: "freebie",
        quantity_sent: raw === "" ? null : Number(raw),
      });
    });

    const payload = {
      item_quantities_sent: payloadItems,
      dispatch_status: status,
      pickup_date: pickupDate || "",
      rescheduled_pickup_date: rescheduledPickupDate || "",
      dispatch_sub_status: status === "dispatched" ? (isRescheduled ? subStatus : "On-Time Dispatch") : "",
      dispatch_sub_status_reason: (status === "dispatched" && isRescheduled && subStatus === "Others") ? subStatusReason.trim() : "",
    };

    setSaving(true);
    try {
      await axios.put(`${API}/dispatch/${dispatchId}`, payload, {
        headers: { Authorization: `Bearer ${getAuthToken()}` },
      });
      toast.success("Dispatch updated");
      onSaved?.();
      onClose?.();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Failed to update dispatch");
    } finally {
      setSaving(false);
    }
  };

  const renderItemTable = (items, kind) => (
    <div className="border rounded-md overflow-hidden">
      <Table>
        <TableHeader>
          <TableRow className="bg-slate-50">
            <TableHead className="text-xs">Product</TableHead>
            <TableHead className="text-xs">SKU</TableHead>
            <TableHead className="text-xs">Brand</TableHead>
            <TableHead className="text-xs text-right">Qty Planned</TableHead>
            <TableHead className="text-xs text-right w-[140px]">Qty Sent *</TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {items.length === 0 ? (
            <TableRow>
              <TableCell colSpan={5} className="text-center text-xs text-muted-foreground py-4">
                No {kind === "sale" ? "sale items" : "freebies"}.
              </TableCell>
            </TableRow>
          ) : (
            items.map((it, idx) => {
              const k = toItemKey({ ...it, item_type: kind }, idx);
              const planned = Number(it.quantity) || 0;
              const partial = isPartial(k, planned);
              return (
                <TableRow key={k} className={partial ? "bg-red-50/60" : ""} data-testid={partial ? `dispatch-row-partial-${kind}-${it.sku_id || idx}` : undefined}>
                  <TableCell className="text-xs">{it.product_name}</TableCell>
                  <TableCell className="text-xs font-mono text-slate-600">{it.sku_id || "—"}</TableCell>
                  <TableCell className="text-xs">{it.brand}</TableCell>
                  <TableCell className="text-xs text-right font-mono">{planned.toLocaleString("en-IN")}</TableCell>
                  <TableCell className="text-right">
                    <Input
                      type="number"
                      min="0"
                      max={planned}
                      step="1"
                      value={qtyByKey[k] ?? ""}
                      onChange={(e) => handleQtyChange(k, planned, e.target.value)}
                      className={`h-8 text-xs font-mono text-right ${partial ? "border-red-300 focus-visible:ring-red-300" : ""}`}
                      data-testid={`dispatch-item-qty-sent-${kind}-${it.sku_id || idx}`}
                    />
                  </TableCell>
                </TableRow>
              );
            })
          )}
        </TableBody>
      </Table>
    </div>
  );

  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose?.()}>
      <DialogContent className="max-w-4xl max-h-[90vh] flex flex-col p-0 overflow-hidden" data-testid="dispatch-edit-modal">
        <DialogHeader className="px-6 pt-5 pb-3 border-b border-slate-200">
          <DialogTitle className="flex items-center gap-2 text-base">
            <Truck className="h-5 w-5 text-[#32B4AC]" />
            Dispatch — {quotation?.quotation_id || "…"}
          </DialogTitle>
          <DialogDescription className="text-[11px] text-muted-foreground">
            Update SKU-level quantities sent, dispatch status, and pickup date for this invoiced quotation.
          </DialogDescription>
          {quotation && (
            <p className="text-[11px] text-muted-foreground">
              {quotation.seller_name} · Invoice {quotation.invoice_number || "—"}
            </p>
          )}
        </DialogHeader>

        <div className="flex-1 overflow-auto px-6 py-4 space-y-5">
          {loading || !quotation ? (
            <div className="py-12 text-center text-xs text-muted-foreground">Loading…</div>
          ) : (
            <>
              {/* Status + Pickup Date */}
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div className="space-y-1.5">
                  <Label className="text-[12px]">Status *</Label>
                  <Select value={status} onValueChange={setStatus}>
                    <SelectTrigger className="h-9" data-testid="dispatch-modal-status-select">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="to_be_dispatch" data-testid="dispatch-modal-status-to-be-dispatch">To be Dispatch</SelectItem>
                      <SelectItem value="dispatched" data-testid="dispatch-modal-status-dispatched">Dispatched</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-1.5">
                  <Label className="text-[12px]">Pickup Date *</Label>
                  <Input
                    type="date"
                    value={pickupDate}
                    onChange={(e) => setPickupDate(e.target.value)}
                    className="h-9"
                    data-testid="dispatch-modal-pickup-date"
                  />
                </div>
                <div className="space-y-1.5">
                  <Label className="text-[12px]">Rescheduled Pickup Date</Label>
                  <Input
                    type="date"
                    value={rescheduledPickupDate}
                    onChange={(e) => handleRescheduledChange(e.target.value)}
                    className="h-9"
                    data-testid="dispatch-modal-rescheduled-pickup-date"
                  />
                </div>
                <div className="space-y-1.5">
                  <Label className="text-[12px]">Final Pickup Date</Label>
                  <div
                    className="h-9 inline-flex w-full items-center rounded-md border border-slate-200 bg-slate-50/60 px-3 text-xs font-medium text-slate-700"
                    data-testid="dispatch-modal-final-pickup-date"
                  >
                    {formatDMY(finalPickupDate)}
                  </div>
                  <p className="text-[10px] text-muted-foreground">Auto: rescheduled date if set, else pickup date.</p>
                </div>
                <div className="space-y-1.5">
                  <Label className="text-[12px]">Total Qty (Sent / Planned)</Label>
                  <div className="h-9 inline-flex items-center rounded-md border border-slate-200 bg-slate-50/40 px-3 text-xs font-mono" data-testid="dispatch-modal-qty-total">
                    <span className="text-slate-800 font-semibold">
                      {qtySentTotal == null ? "—" : qtySentTotal.toLocaleString("en-IN")}
                    </span>
                    <span className="text-slate-400 mx-1">/</span>
                    <span className="text-slate-600">{qtyPlannedTotal.toLocaleString("en-IN")}</span>
                  </div>
                </div>
              </div>

              {/* Dispatch Sub-Status (reason behind a reschedule) — only relevant once Dispatched */}
              {status === "dispatched" && (
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div className="space-y-1.5">
                  <Label className="text-[12px]">
                    Dispatch Sub-Status {isRescheduled && <span className="text-red-500">*</span>}
                  </Label>
                  <Select value={subStatus} onValueChange={setSubStatus} disabled={!isRescheduled}>
                    <SelectTrigger className="h-9" data-testid="dispatch-modal-sub-status-select">
                      <SelectValue placeholder="Select a reason" />
                    </SelectTrigger>
                    <SelectContent>
                      {SUB_STATUS_OPTIONS
                        .filter((o) => (isRescheduled ? o !== "On-Time Dispatch" : true))
                        .map((o) => (
                          <SelectItem key={o} value={o}>{o}</SelectItem>
                        ))}
                    </SelectContent>
                  </Select>
                  {!isRescheduled && (
                    <p className="text-[10px] text-muted-foreground">On-Time Dispatch — add a Rescheduled Pickup Date to record a delay reason.</p>
                  )}
                </div>
                {isRescheduled && subStatus === "Others" && (
                  <div className="space-y-1.5 md:col-span-2">
                    <Label className="text-[12px]">Reason <span className="text-red-500">*</span></Label>
                    <Input
                      value={subStatusReason}
                      onChange={(e) => setSubStatusReason(e.target.value)}
                      placeholder="Enter the reason"
                      className="h-9"
                      data-testid="dispatch-modal-sub-status-reason"
                    />
                  </div>
                )}
              </div>
              )}

              {/* Validation banner */}
              {validationErrors.length > 0 && (
                <div className="flex items-start gap-2 rounded-md border border-red-200 bg-red-50 px-3 py-2 text-[12px] text-red-800" data-testid="dispatch-modal-validation-error">
                  <AlertCircle className="h-4 w-4 mt-px shrink-0" />
                  <div>
                    <div className="font-semibold mb-0.5">Cannot mark as Dispatched:</div>
                    <ul className="list-disc list-inside space-y-0.5">
                      {validationErrors.map((e) => <li key={e}>{e}</li>)}
                    </ul>
                  </div>
                </div>
              )}

              {/* Sale items */}
              <div className="space-y-2">
                <div className="flex items-center justify-between gap-2">
                  <div className="flex items-center gap-2">
                    <h3 className="text-[13px] font-semibold text-slate-800">Sale Items</h3>
                    <span className="text-[11px] text-muted-foreground">({(quotation.items || []).length})</span>
                  </div>
                  <button
                    type="button"
                    onClick={handleSelectAll}
                    className="text-[11px] font-medium text-[#32B4AC] hover:text-[#249a92] underline-offset-2 hover:underline disabled:opacity-50 disabled:no-underline"
                    disabled={saving}
                    data-testid="dispatch-modal-select-all"
                  >
                    Select All (Qty Sent = Qty Planned)
                  </button>
                </div>
                {renderItemTable(quotation.items || [], "sale")}
              </div>

              {/* Freebies */}
              <div className="space-y-2">
                <div className="flex items-center gap-2">
                  <Gift className="h-4 w-4 text-slate-500" />
                  <h3 className="text-[13px] font-semibold text-slate-800">Freebies</h3>
                  <span className="text-[11px] text-muted-foreground">({(quotation.freebies || []).length})</span>
                </div>
                {renderItemTable(quotation.freebies || [], "freebie")}
              </div>
            </>
          )}
        </div>

        <DialogFooter className="px-6 py-3 border-t border-slate-200">
          <Button variant="ghost" onClick={onClose} disabled={saving} data-testid="dispatch-modal-cancel">
            Cancel
          </Button>
          <Button
            onClick={handleSave}
            disabled={saving || loading || !quotation}
            className="bg-[#32B4AC] hover:bg-[#2A9A93] text-white"
            data-testid="dispatch-modal-save"
          >
            <Save className="h-4 w-4 mr-1.5" />
            {saving ? "Saving…" : "Save"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
